import type {
  CommendaAiConversationAccessView,
  CommendaAiConversationVisibility,
} from '@commenda/api/commenda-ai/types';
import { Dropdown, Icons } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useEffect, useRef, useState } from 'react';
import { ConversationVisibilityControl } from './ConversationVisibilityControl';

export function ConversationHeader({
  title,
  access,
  workspaceName,
  onDelete,
  onRename,
  onVisibilityChange,
  trailing,
}: {
  title: string;
  access?: CommendaAiConversationAccessView;
  workspaceName?: string;
  /** Absent when the viewer may not delete (they do not own the conversation). */
  onDelete?: () => void;
  onRename?: (title: string) => void;
  /** Absent when the viewer may not change who can see it: non-owners, and scheduled-agent run
   * sessions, whose visibility follows the agent. */
  onVisibilityChange?: (visibility: CommendaAiConversationVisibility) => void;
  trailing?: React.ReactNode;
}) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);
  const renamingRef = useRef(false);

  useEffect(() => {
    if (isRenaming) inputRef.current?.select();
  }, [isRenaming]);

  const startRename = () => {
    renamingRef.current = true;
    setDraft(title);
    setIsRenaming(true);
  };
  // save=true commits, false discards. Guarded by a ref so the blur fired when the input unmounts
  // (after Enter/Escape) can't double-commit or override a cancel.
  const finishRename = (save: boolean) => {
    if (!renamingRef.current) return;
    renamingRef.current = false;
    setIsRenaming(false);
    const next = draft.trim();
    if (save && next && next !== title) onRename?.(next);
    else setDraft(title);
  };

  const menuItems = [
    ...(onRename
      ? [{ label: 'Rename', icon: Icons.Edit05, onClick: startRename }]
      : []),
    ...(onDelete
      ? [
          {
            label: 'Delete',
            icon: Icons.Trash01,
            className:
              'text-text-error hover:text-text-error focus:text-text-error',
            onClick: onDelete,
          },
        ]
      : []),
  ];

  return (
    // The <h1> stays mounted (just hidden) while renaming so it keeps defining the row height — the
    // input is an absolute overlay, so its form-control metrics never grow the header box.
    <div className="relative flex items-center gap-sm px-4 py-3">
      <h1
        className={cn(
          'min-w-0 truncate txt-small-medium text-text-primary',
          isRenaming && 'invisible'
        )}
      >
        {title}
      </h1>
      {isRenaming ? (
        // Absolute overlay over the (hidden) h1 so the header never shifts. The field hugs its
        // content: an invisible sizer mirrors the draft (same font/padding/border) to drive the
        // width, clamped between a small min and the available row width.
        <div className="absolute top-1/2 left-4 flex min-w-[4rem] max-w-[calc(100%-2rem)] -translate-y-1/2">
          <span
            aria-hidden="true"
            className="invisible whitespace-pre border-[0.5px] border-transparent px-2 py-1 txt-small-medium"
          >
            {draft || ' '}
          </span>
          <input
            ref={inputRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                finishRename(true);
              } else if (event.key === 'Escape') {
                event.preventDefault();
                finishRename(false);
              }
            }}
            onBlur={() => finishRename(true)}
            aria-label="Rename conversation"
            className="absolute inset-0 w-full min-w-0 rounded-md border-[0.5px] border-border-primary bg-bg-primary px-2 py-1 txt-small-medium text-text-primary outline-none transition-colors focus:border-fg-brand"
          />
        </div>
      ) : null}
      {!isRenaming && menuItems.length > 0 ? (
        <Dropdown
          side="bottom"
          align="start"
          sideOffset={4}
          menu={{ groups: [{ items: menuItems }] }}
        >
          <button
            type="button"
            aria-label="Conversation options"
            className="inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-fg-quaternary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
          >
            <Icons.DotsHorizontal className="size-3.5" aria-hidden="true" />
          </button>
        </Dropdown>
      ) : null}
      {access || trailing ? (
        <div className="ml-auto flex shrink-0 items-center gap-sm">
          {access ? (
            <ConversationVisibilityControl
              access={access}
              workspaceName={workspaceName}
              onChange={onVisibilityChange}
            />
          ) : null}
          {trailing}
        </div>
      ) : null}
    </div>
  );
}
